import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

const RENEW_DEBOUNCE_MS = 30000;
const RENEW_BEFORE_EXPIRY_SEC = 120;

/**
 * Proactively renew OIDC access tokens on expiry warning, tab focus, and visibility return.
 * Uses refresh_token when available; falls back to silent iframe renew via oidc-client-ts.
 */
export function useSessionRenewal(): void {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const lastRenewAttemptRef = useRef(0);
  const renewingRef = useRef(false);

  useEffect(() => {
    const trySilentRenew = async () => {
      const a = authRef.current;
      if (!a.user || a.isLoading || a.activeNavigator) return;

      const expiresIn = a.user.expires_in;
      const needsRenew =
        a.user.expired ||
        (expiresIn !== undefined && expiresIn < RENEW_BEFORE_EXPIRY_SEC);
      if (!needsRenew) return;

      const now = Date.now();
      if (now - lastRenewAttemptRef.current < RENEW_DEBOUNCE_MS) return;
      if (renewingRef.current) return;

      lastRenewAttemptRef.current = now;
      renewingRef.current = true;
      try {
        await a.signinSilent();
      } catch (err) {
        console.warn('[auth] Session renewal failed:', err);
      } finally {
        renewingRef.current = false;
      }
    };

    const onExpiring = () => {
      void trySilentRenew();
    };
    const onSilentRenewError = (err: Error) => {
      console.warn('[auth] Silent renew error:', err);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void trySilentRenew();
    };
    const onFocus = () => void trySilentRenew();

    auth.events.addAccessTokenExpiring(onExpiring);
    auth.events.addSilentRenewError(onSilentRenewError);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      auth.events.removeAccessTokenExpiring(onExpiring);
      auth.events.removeSilentRenewError(onSilentRenewError);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [auth.events]);
}
